import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// --- Add FaPercent, FaSave, FaImage, FaUserCircle ---
import { FaUser, FaFileAlt, FaFileContract, FaArrowLeft, FaSpinner, FaExclamationTriangle, FaCheck, FaUsers, FaPercent, FaSave, FaImage, FaUserCircle, FaExternalLinkAlt } from 'react-icons/fa';
// --- Import rmApi and productApi ---
import { rmApi, productApi } from '../../services/api'; // Adjust path if needed
// --- Import Swal ---
import Swal from 'sweetalert2';

// --- Add necessary components if using a UI library, e.g., Alert for messages ---
// import { Alert } from 'react-bootstrap'; // Example if using react-bootstrap

function OwnerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- Add state for agents ---
  const [agentsList, setAgentsList] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState('');
  // Add pagination state here if implementing agent list pagination

  // --- Add state for products and commissions ---
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [bankFilter, setBankFilter] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  
  // State for active product category tab (moved from renderTabContent)
  const [activeCommissionCategory, setActiveCommissionCategory] = useState('');

  // Commission data structure now handles both percentage and amount values
  const [currentCommissions, setCurrentCommissions] = useState({}); // Store as { productId: { percentage, amount } }
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionsError, setCommissionsError] = useState('');

  const [commissionInputs, setCommissionInputs] = useState({}); // Store input values { productId: value }

  const [savingCommissions, setSavingCommissions] = useState(false);
  // const [saveSuccess, setSaveSuccess] = useState(''); // Removed saveSuccess state
  const [saveError, setSaveError] = useState('');
  // --- End of new state ---

  // Fetch DSO owner details (existing useEffect)
  useEffect(() => {
    const fetchOwnerDetails = async () => {
      setLoading(true);
      setError('');
      try {
        // Call the API to get DSO details
        const response = await rmApi.getDsoDetails(id);
        
        // Transform the API response to match our component's expected structure
        const dsoData = response.data;
        const profile = dsoData.dso_profile || {};
        
        // Get the location information from nested objects in the profile
        const cityName = profile?.city_name || 'N/A'; // Use optional chaining
        const stateName = profile?.state_name || 'N/A'; // Use optional chaining
        const location = `${cityName}, ${stateName}`;

        // Determine status
        let status = "Unknown";
        if (!dsoData.is_active) {
          status = "Inactive";
        } else if (profile.is_verified) {
          status = "Verified";
        } else {
          status = "Pending Verification";
        }
        
        // Format documents
        const documents = [
          { 
            id: 1, 
            name: "Aadhaar Card", 
            status: profile.adhaar_card_front_file ? "Verified" : "Pending", 
            url: profile.adhaar_card_front_file || "#" 
          },
          { 
            id: 2, 
            name: "PAN Card", 
            status: profile.pan_card_file ? "Verified" : "Pending", 
            url: profile.pan_card_file || "#" 
          }
        ];
        
        // Add company certificate document if business type
        if (profile.applicant_type === "Business" && profile.company_certificate_file) {
          documents.push({
            id: 3,
            name: "Business Certificate",
            status: "Verified",
            url: profile.company_certificate_file
          });
        }
        
        // Format agreement information
        const agreementSigned = profile.agreement_signed || false;
        const agreementDate = profile.agreement_signed_date;
        
        // Set owner details with additional profile information
        setOwnerDetails({
          id: dsoData.id,
          name: dsoData.name || "N/A",
          email: dsoData.email || "N/A",
          phone: dsoData.mobile || "N/A",
          location: location,
          state: stateName,
          city: cityName,
          address: profile.address || "N/A",
          status: status,
          // Additional profile fields
          applicantType: profile.applicant_type || "N/A",
          businessType: profile.business_type || "N/A",
          individualType: profile.individual_type || "N/A",
          panNumber: profile.pan_card_no || "N/A",
          aadhaarNumber: profile.adhaar_card_no || "N/A",
          gstinNumber: profile.gstin_no || "N/A",
          documents: documents,
          agreement: {
            signed: agreementSigned,
            date: agreementDate ? new Date(agreementDate).toLocaleDateString() : "N/A",
            version: profile.agreement_version || "1.0",
            url: profile.agreement_file || "#"
          }
        });
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching DSO details:", err);
        setError('Failed to load DSO details. Please try again later.');
        setLoading(false);
      }
    };

    fetchOwnerDetails();
  }, [id]);

  // --- Add useEffect to fetch agents AFTER owner details are loaded ---
  useEffect(() => {
    const fetchAgents = async () => {
      if (!ownerDetails || !ownerDetails.id) {
        // Don't fetch agents if owner details haven't loaded or lack an ID
        return;
      }

      setAgentsLoading(true);
      setAgentsError('');
      setAgentsList([]); // Clear previous list

      try {
        // Use the ownerDetails.id which corresponds to the dso_id
        const response = await rmApi.getDsoAgents(ownerDetails.id, { limit: 50 }); // Fetch agents for this DSO, adjust limit as needed
        setAgentsList(response.data.items || []);
      } catch (err) {
        console.error("Error fetching agents for DSO:", err);
        setAgentsError('Failed to load assigned agents.');
      } finally {
        setAgentsLoading(false);
      }
    };

    // Trigger fetchAgents when ownerDetails are successfully loaded
    // Check ownerDetails is not null, loading is false, and there's no main error
    if (ownerDetails && !loading && !error) {
        fetchAgents();
    }
    // Dependency array includes ownerDetails to refetch if it changes (though unlikely here)
    // Also include loading and error to ensure fetch runs when they change to a success state
  }, [ownerDetails, loading, error]); // Depend on ownerDetails state and loading/error flags

  // --- Add useEffect to fetch all products ---
  useEffect(() => {
    const fetchProducts = async () => {
      setProductsLoading(true);
      setProductsError('');
      try {
        // Fetch all available products - adjust limit if needed
        const response = await productApi.getProducts({ limit: 200 });
        const productItems = response.data.items || [];
        setProducts(productItems);
        setFilteredProducts(productItems); // Initialize filtered products with all products
      } catch (err) {
        console.error("Error fetching products:", err);
        setProductsError('Failed to load products list.');
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []); // Fetch products once on mount

  // --- Add useEffect to fetch current commissions for this DSO ---
  useEffect(() => {
    const fetchCurrentCommissions = async () => {
      // Only fetch if we have the DSO ID
      if (!id) return;

      setCommissionsLoading(true);
      setCommissionsError('');
      setCurrentCommissions({}); // Reset
      try {
        const response = await rmApi.getDsoCommissions(id);
        // Check if response.data is an object with 'items' array (format from DSOCommissionListResponse)
        const commissions = Array.isArray(response.data) ? response.data : 
                          (response.data && Array.isArray(response.data.items)) ? response.data.items : [];
        
        // Convert it to a map for easier lookup, now handling both percentage and amount
        const commissionsMap = commissions.reduce((acc, comm) => {
          // Store both commission_percentage and commission_amount if they exist
          if (comm.product_id !== undefined) {
            acc[comm.product_id] = {
              percentage: comm.commission_percentage !== undefined ? comm.commission_percentage : null,
              amount: comm.commission_amount !== undefined ? comm.commission_amount : null
            };
          }
          return acc;
        }, {});
        setCurrentCommissions(commissionsMap);
      } catch (err) {
        // Handle 404 (no commissions set yet) gracefully
        if (err.response && err.response.status === 404) {
           console.log("No existing commissions found for this DSO.");
           setCurrentCommissions({}); // Ensure it's an empty object
        } else {
          console.error("Error fetching current commissions:", err);
          setCommissionsError('Failed to load current commission settings.');
        }
      } finally {
        setCommissionsLoading(false);
      }
    };

    fetchCurrentCommissions();
  }, [id]); // Refetch if DSO ID changes

  // --- Add useEffect to initialize/update commissionInputs when products or currentCommissions load ---
  useEffect(() => {
    // Only initialize inputs when products are loaded and commissions are not loading
    if (products.length > 0 && !commissionsLoading) {
      const initialInputs = {};
      products.forEach(product => {
        const categoryName = product.category?.category || 'Other';
        const isCardProduct = categoryName === 'Credit Card';
        
        // Check if we have commission data for this product
        if (currentCommissions[product.id] !== undefined) {
          // For Credit Card products, use the amount if available
          if (isCardProduct && currentCommissions[product.id].amount !== null) {
            initialInputs[product.id] = String(currentCommissions[product.id].amount);
          } 
          // For other products, use the percentage if available
          else if (!isCardProduct && currentCommissions[product.id].percentage !== null) {
            initialInputs[product.id] = String(currentCommissions[product.id].percentage);
          }
          // Fallbacks in case the expected field isn't available
          else if (currentCommissions[product.id].percentage !== null) {
            initialInputs[product.id] = String(currentCommissions[product.id].percentage);
          }
          else if (currentCommissions[product.id].amount !== null) {
            initialInputs[product.id] = String(currentCommissions[product.id].amount);
          }
          else {
            initialInputs[product.id] = '';
          }
        } else {
          initialInputs[product.id] = ''; // Default to empty string
        }
      });
      setCommissionInputs(initialInputs);
    }
    // Add commissionsLoading to dependency array to ensure this runs AFTER commissions are loaded/updated
  }, [products, currentCommissions, commissionsLoading]); // Re-run when products, current commissions, or loading state change
  
  // Set active commission category when products change or if current category has no products
  useEffect(() => {
    if (filteredProducts.length > 0) {
      // Group products by category
      const tempProductsByCategory = filteredProducts.reduce((acc, product) => {
        const categoryName = product.category?.category || 'Other';
        if (!acc[categoryName]) {
          acc[categoryName] = [];
        }
        acc[categoryName].push(product);
        return acc;
      }, {});
      
      // Sort categories to make sure Credit Card appears first, then alphabetically
      const tempSortedCategories = Object.keys(tempProductsByCategory).sort((a, b) => {
        if (a === 'Credit Card') return -1;
        if (b === 'Credit Card') return 1;
        return a.localeCompare(b);
      });
      
      // If no active category or the active category has no products, set to first category
      if (tempSortedCategories.length > 0 && 
          (!activeCommissionCategory || !tempProductsByCategory[activeCommissionCategory])) {
        setActiveCommissionCategory(tempSortedCategories[0]);
      }
    }
  }, [filteredProducts, activeCommissionCategory]); // Depend on filteredProducts and activeCommissionCategory


  // --- Handler for bank filter changes ---
  const handleBankFilterChange = (selectedBank) => {
    setBankFilter(selectedBank);
    if (!selectedBank) {
      // If no bank is selected, show all products
      setFilteredProducts(products);
    } else {
      // Filter products by selected bank
      setFilteredProducts(products.filter(product => 
        product.bank && product.bank.bank === selectedBank
      ));
    }
  };

  // --- Handler for commission input changes ---
  const handleCommissionChange = (productId, value, isCardProduct = false) => {
    // Allow empty string, numbers, and a single decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const numericValue = parseFloat(value);
      
      if (isCardProduct) {
        // For credit card products (fixed amount)
        // No upper limit for amount, just ensure it's not negative
        if (value === '' || (numericValue >= 0)) {
          setCommissionInputs(prevInputs => ({
            ...prevInputs,
            [productId]: value,
          }));
          setSaveError('');
        } else if (numericValue < 0) {
          // Prevent negative values
          setCommissionInputs(prevInputs => ({ ...prevInputs, [productId]: '0' }));
        }
      } else {
        // For other products (percentage)
        // Range validation: 0-100 for percentage
        if (value === '' || (numericValue >= 0 && numericValue <= 100)) {
          setCommissionInputs(prevInputs => ({
            ...prevInputs,
            [productId]: value,
          }));
          setSaveError('');
        } else if (numericValue > 100) {
          // Cap at 100% for percentages
          setCommissionInputs(prevInputs => ({ ...prevInputs, [productId]: '100' }));
        } else if (numericValue < 0) {
          // Prevent negative percentages
          setCommissionInputs(prevInputs => ({ ...prevInputs, [productId]: '0' }));
        }
      }
    }
  };

  // --- Handler for saving commissions ---
  const handleSaveCommissions = async () => {
    setSavingCommissions(true);
    setSaveError('');

    // Log commissionsInputs for debugging
    console.log('Current commission inputs:', commissionInputs);

    // Prepare data payload
    // Format based on product category: Credit Card products use commission_amount, others use commission_percentage
    const payload = Object.entries(commissionInputs)
      .map(([productId, value]) => {
        const trimmedValue = value?.toString().trim() || '';
        const numericValue = parseFloat(trimmedValue);
        
        // Skip if empty or invalid number
        if (trimmedValue === '' || isNaN(numericValue) || numericValue < 0) {
          return null;
        }
        
        // Find the product to determine its category
        const product = products.find(p => p.id == productId);
        if (!product) return null;
        
        const categoryName = product.category?.category || 'Other';
        const isCardProduct = categoryName === 'Credit Card';
        
        // Create different payload based on product category
        return {
          product_id: parseInt(productId, 10),
          // For Credit Card products, use commission_amount
          // For other products, use commission_percentage
          ...(isCardProduct 
            ? { commission_amount: numericValue }
            : { commission_percentage: numericValue })
        };
      })
      .filter(item => item !== null); // Remove null entries

    console.log('Sending commission payload:', payload);

    // Check if there are any commissions to save
    if (payload.length === 0) {
      setSaveError('No commission values to save. Please enter at least one valid commission value.');
      setSavingCommissions(false);
      return;
    }

    try {
      // Call the API to save commissions - this now returns an array of responses
      const saveResponses = await rmApi.setDsoCommissions(id, payload);
      console.log('Commission save responses:', saveResponses);
      
      // Check if all responses were successful
      const hasErrors = saveResponses.some(response => {
        // Consider any response with status >= 400 as an error
        if (response && response.status && response.status >= 400) {
          return true;
        }
        return false;
      });
      
      if (hasErrors) {
        // --- Show SweetAlert for partial success ---
        Swal.fire({
          title: 'Partial Success',
          text: 'Some commissions may not have been saved correctly.',
          icon: 'warning',
          timer: 3000,
          showConfirmButton: true
        });
      } else {
        // --- Show SweetAlert on success ---
        Swal.fire({
          title: 'Success!',
          text: 'Commission settings updated successfully!',
          icon: 'success',
          timer: 2000, // Auto close after 2 seconds
          showConfirmButton: false
        });
      }
      // --- End SweetAlert ---

      // Refetch current commissions to update the state
      const response = await rmApi.getDsoCommissions(id);
      console.log('Updated commissions response:', response);
      
      // Handle different response formats
      const commissions = Array.isArray(response.data) ? response.data : 
                        (response.data && Array.isArray(response.data.items)) ? response.data.items : [];
      
      // Create a map of product_id to commission data (percentage and amount) for easier lookup
      const commissionsMap = commissions.reduce((acc, comm) => {
        // Ensure we have valid product_id
        if (comm && typeof comm.product_id !== 'undefined') {
          // Store both commission percentage and amount
          acc[comm.product_id] = {
            percentage: comm.commission_percentage !== undefined ? comm.commission_percentage : null,
            amount: comm.commission_amount !== undefined ? comm.commission_amount : null
          };
        }
        return acc;
      }, {});
      
      console.log('Updated commissions map:', commissionsMap);
      
      // Update the currentCommissions state
      setCurrentCommissions(commissionsMap);
      
      // Also directly update the commissionInputs to reflect saved values
      setCommissionInputs(prevInputs => {
        const newInputs = { ...prevInputs };
        
        // Loop through products to ensure we use the right value for each product type
        products.forEach(product => {
          const productId = product.id;
          if (commissionsMap[productId]) {
            const categoryName = product.category?.category || 'Other';
            const isCardProduct = categoryName === 'Credit Card';
            
            // Choose the appropriate value based on product category
            if (isCardProduct && commissionsMap[productId].amount !== null) {
              newInputs[productId] = commissionsMap[productId].amount.toString();
            } else if (!isCardProduct && commissionsMap[productId].percentage !== null) {
              newInputs[productId] = commissionsMap[productId].percentage.toString();
            }
            // Fallbacks if the primary value isn't available
            else if (commissionsMap[productId].percentage !== null) {
              newInputs[productId] = commissionsMap[productId].percentage.toString();
            }
            else if (commissionsMap[productId].amount !== null) {
              newInputs[productId] = commissionsMap[productId].amount.toString();
            }
          }
        });
        
        return newInputs;
      });

    } catch (err) {
      console.error("Error saving commissions:", err);
      
      // Extract error message from response if available
      let errorMsg = 'Failed to save commission settings. Please try again.';
      
      if (err.response) {
        if (err.response.data && err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.response.status === 400) {
          errorMsg = 'Invalid commission values. Please check your inputs and try again.';
        } else if (err.response.status === 401 || err.response.status === 403) {
          errorMsg = 'You do not have permission to update commission settings.';
        } else if (err.response.status === 404) {
          errorMsg = 'DSO not found or no longer available.';
        } else if (err.response.status >= 500) {
          errorMsg = 'Server error. Please try again later.';
        }
      }
      
      setSaveError(errorMsg);
    } finally {
      setSavingCommissions(false);
    }
  };


  // Go back to owners list (existing function)
  const handleBack = () => {
    navigate('/rm/owners');
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    // ... (keep existing loading/error checks for ownerDetails) ...

    if (loading && !ownerDetails) { // Show main loading only if ownerDetails are not yet loaded
      return (
        <div className="rm-loading-state">
          <FaSpinner className="rm-spinner" />
          <span>Loading DSO details...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="rm-error-state">
          <FaExclamationTriangle className="rm-error-icon" />
          <span>{error}</span>
        </div>
      );
    }

    if (!ownerDetails) {
      return <div className="rm-no-data">No details available</div>;
    }

    switch (activeTab) {
      case 'profile':
        // --- Keep existing profile content ---
        return (
          <div className="rm-owner-detail-content">
            <h4>Profile Information</h4>
            <div className="rm-profile-details rm-profile-two-column">
              {/* Left Column - Basic Information */}
              <div className="rm-profile-column">
                <h5 className="rm-column-heading">Basic Information</h5>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Name:</span>
                  <span className="rm-detail-value">{ownerDetails.name || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Email:</span>
                  <span className="rm-detail-value">{ownerDetails.email || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Phone:</span>
                  <span className="rm-detail-value">{ownerDetails.phone || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Status:</span>
                  <span className="rm-detail-value">
                    {ownerDetails.status === 'Verified' ? (
                      <span className="rm-status-badge rm-status-verified">
                        {ownerDetails.status}
                      </span>
                    ) : ownerDetails.status === 'Active' ? (
                      <span className="rm-status-badge rm-status-active">
                        {ownerDetails.status}
                      </span>
                    ) : ownerDetails.status === 'Pending' ? (
                      <span className="rm-status-badge rm-status-pending">
                        {ownerDetails.status}
                      </span>
                    ) : ownerDetails.status === 'Inactive' ? (
                      <span className="rm-status-badge rm-status-inactive">
                        {ownerDetails.status}
                      </span>
                    ) : (
                      ownerDetails.status || 'N/A'
                    )}
                  </span>
                </div>
                
                {/* Location information */}
                <h5 className="rm-column-heading">Location Information</h5>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">State:</span>
                  <span className="rm-detail-value">{ownerDetails.state || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">City:</span>
                  <span className="rm-detail-value">{ownerDetails.city || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Address:</span>
                  <span className="rm-detail-value">{ownerDetails.address || 'N/A'}</span>
                </div>
              </div>
              
              {/* Right Column - Business/Identification Information */}
              <div className="rm-profile-column">
                <h5 className="rm-column-heading">Business/Applicant Details</h5>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Applicant Type:</span>
                  <span className="rm-detail-value">{ownerDetails.applicantType || 'N/A'}</span>
                </div>
                {ownerDetails.applicantType === 'Business' && (
                  <div className="rm-detail-item">
                    <span className="rm-detail-label">Business Type:</span>
                    <span className="rm-detail-value">{ownerDetails.businessType || 'N/A'}</span>
                  </div>
                )}
                {ownerDetails.applicantType === 'Individual' && (
                  <div className="rm-detail-item">
                    <span className="rm-detail-label">Individual Type:</span>
                    <span className="rm-detail-value">{ownerDetails.individualType || 'N/A'}</span>
                  </div>
                )}
                {ownerDetails.gstinNumber && ownerDetails.gstinNumber !== 'N/A' && (
                  <div className="rm-detail-item">
                    <span className="rm-detail-label">GSTIN Number:</span>
                    <span className="rm-detail-value">{ownerDetails.gstinNumber}</span>
                  </div>
                )}
                
                <h5 className="rm-column-heading">Identification Details</h5>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">PAN Number:</span>
                  <span className="rm-detail-value">{ownerDetails.panNumber || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Aadhaar Number:</span>
                  <span className="rm-detail-value">{ownerDetails.aadhaarNumber || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'documents':
        return (
          <div className="rm-owner-detail-content">
            <h4>Document Verification</h4>
            {ownerDetails.documents && ownerDetails.documents.length > 0 ? (
              <table className="rm-documents-table">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerDetails.documents.map(doc => (
                    <tr key={doc.id}>
                      <td>{doc.name}</td>
                      <td>
                        {doc.status === 'Verified' ? (
                          <span className="rm-status-badge rm-status-verified">Verified</span>
                        ) : doc.status === 'Pending' ? (
                          <span className="rm-status-badge rm-status-pending">Pending</span>
                        ) : (
                          doc.status
                        )}
                      </td>
                      <td>
                        <a href={doc.url} className="rm-view-doc-btn" target="_blank" rel="noopener noreferrer">
                          <FaFileAlt /> View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="rm-no-data">No documents available</p>
            )}
          </div>
        );
      case 'agreement':
        return (
          <div className="rm-owner-detail-content">
            <h4>DSO Agreement</h4>
            {ownerDetails.agreement ? (
              <div className="rm-profile-details">
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Status:</span>
                  <span className="rm-detail-value">
                    {ownerDetails.agreement.signed ? (
                      <span className="rm-status-badge rm-status-verified">
                        <FaCheck className="rm-icon-small" /> Signed
                      </span>
                    ) : (
                      <span className="rm-status-badge rm-status-pending">Pending</span>
                    )}
                  </span>
                </div>
                {ownerDetails.agreement.signed && (
                  <>
                    <div className="rm-detail-item">
                      <span className="rm-detail-label">Signed Date:</span>
                      <span className="rm-detail-value">
                        {ownerDetails.agreement.date}
                      </span>
                    </div>
                    <div className="rm-detail-item">
                      <span className="rm-detail-label">Version:</span>
                      <span className="rm-detail-value">
                        {ownerDetails.agreement.version}
                      </span>
                    </div>
                  </>
                )}
                <div className="rm-detail-item">
                  {ownerDetails.agreement.url && ownerDetails.agreement.url !== "#" ? (
                    <a 
                      href={ownerDetails.agreement.url} 
                      className="rm-btn rm-btn-primary" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <FaFileContract /> View Agreement Document
                    </a>
                  ) : (
                    <button 
                      className="rm-btn rm-btn-secondary" 
                      disabled
                    >
                      <FaFileContract /> No Agreement Document Available
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className="rm-no-data">No agreement information available</p>
            )}
          </div>
        );
      // --- Add case for 'agents' tab ---
      case 'agents':
        return (
          <div className="rm-owner-detail-content">
            <h4>Assigned Agents</h4>
            {agentsLoading ? (
              <div className="rm-loading-state rm-loading-state-small">
                <FaSpinner className="rm-spinner" />
                <span>Loading agents...</span>
              </div>
            ) : agentsError ? (
              <div className="rm-error-state rm-error-state-small">
                <FaExclamationTriangle className="rm-error-icon" />
                <span>{agentsError}</span>
              </div>
            ) : agentsList.length > 0 ? (
              <table className="rm-agents-table"> {/* Use a specific class */}
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    {/* Add other columns if needed */}
                  </tr>
                </thead>
                <tbody>
                  {agentsList.map(agent => (
                    <tr key={agent.id}>
                      <td>{agent.name || 'N/A'}</td>
                      <td>{agent.email || 'N/A'}</td>
                      <td>{agent.mobile || 'N/A'}</td>
                      {/* Add other cells if needed */}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="rm-no-data">No agents assigned to this DSO.</p>
            )}
            {/* Add pagination controls here if implemented */}
          </div>
        );

      // --- Add case for 'commissions' tab ---
      case 'commissions':
        // Show loading indicator specifically for products/commissions if main details are loaded
        if (productsLoading || commissionsLoading) {
          return (
            <div className="rm-loading-state rm-loading-state-small">
              <FaSpinner className="rm-spinner" />
              <span>Loading commission settings...</span>
            </div>
          );
        }
        // Show error specific to products/commissions
        if (productsError || commissionsError) {
          return (
            <div className="rm-error-state rm-error-state-small">
              <FaExclamationTriangle className="rm-error-icon" />
              <span>{productsError || commissionsError}</span>
            </div>
          );
        }
        // Handle case where no products are available
        if (products.length === 0 && !productsLoading) {
            return <p className="rm-no-data">No products found to set commissions for.</p>;
        }

        // Group products by category
        const productsByCategory = filteredProducts.reduce((acc, product) => {
          const categoryName = product.category?.category || 'Other';
          if (!acc[categoryName]) {
            acc[categoryName] = [];
          }
          acc[categoryName].push(product);
          return acc;
        }, {});

        // Sort categories to make sure Credit Card appears first, then alphabetically
        const sortedCategories = Object.keys(productsByCategory).sort((a, b) => {
          if (a === 'Credit Card') return -1;
          if (b === 'Credit Card') return 1;
          return a.localeCompare(b);
        });

        return (
          <div className="rm-owner-detail-content rm-commissions-tab">
            <h4>Set Product Commissions</h4>
            <p className="rm-commission-instructions">
              Enter the commission values for each product. For Credit Card products, enter the fixed amount (₹). For other products, enter the percentage (%). Leave blank or set to 0 if no commission applies.
            </p>

            {/* Display Save Success/Error Messages */}
            {saveError && <div className="rm-alert rm-alert-danger">{saveError}</div>}

            <div className="rm-commission-filters">
              <div className="rm-product-count">
                Showing <strong>{filteredProducts.length}</strong> of <strong>{products.length}</strong> products
              </div>
              <div className="rm-bank-filter">
                <label htmlFor="bankFilter">Filter by Bank: </label>
                <select 
                  id="bankFilter" 
                  value={bankFilter} 
                  onChange={(e) => handleBankFilterChange(e.target.value)}
                >
                  <option value="">All Banks</option>
                  {/* Create unique list of bank names */}
                  {Array.from(new Set(products.map(p => p.bank?.bank).filter(Boolean))).map(bankName => (
                    <option key={bankName} value={bankName}>{bankName}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Show message if no products match filter */}
            {sortedCategories.length === 0 ? (
              <p className="rm-no-data">No products match the current filter.</p>
            ) : (
              <>
                {/* Tabs for product categories */}
                <div className="rm-commission-category-tabs">
                  {sortedCategories.map(categoryName => (
                    <div
                      key={categoryName}
                      className={`rm-commission-category-tab ${activeCommissionCategory === categoryName ? 'active' : ''}`}
                      onClick={() => setActiveCommissionCategory(categoryName)}
                    >
                      {categoryName}
                      <span className="rm-commission-tab-count">{productsByCategory[categoryName].length}</span>
                    </div>
                  ))}
                </div>
                
                {/* Products for selected category */}
                {activeCommissionCategory && productsByCategory[activeCommissionCategory] && (
                  <div className="rm-commission-form">
                    {productsByCategory[activeCommissionCategory].map(product => {
                      const isCardProduct = product.category?.category === 'Credit Card';
                      
                      return (
                        <div key={product.id} className="rm-commission-product-item">
                          <div className="rm-commission-product-image">
                            {product.product_image_url ? (
                              <img src={product.product_image_url} alt={product.name} />
                            ) : (
                              <FaImage className="rm-placeholder-icon" />
                            )}
                          </div>
                          <div className="rm-commission-product-info">
                            <div className="rm-commission-product-name-container">
                              <span className="rm-commission-product-name">{product.name}</span>
                              {product.apply_url && (
                                <a 
                                  href={product.apply_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="rm-apply-link"
                                  title="Open application link"
                                >
                                  <FaExternalLinkAlt size={14} />
                                </a>
                              )}
                            </div>
                            <div className="rm-commission-product-details">
                              <span className="rm-commission-product-bank">
                                <strong>Bank:</strong> {product.bank?.bank || 'N/A'}
                              </span>
                              {product.credit_card_type && (
                                <span className="rm-commission-product-type">
                                  <strong>Type:</strong> {product.credit_card_type}
                                </span>
                              )}
                              {product.annual_fees && (
                                <span className="rm-commission-product-fees">
                                  <strong>Annual Fee:</strong> ₹{product.annual_fees}
                                </span>
                              )}
                            </div>
                            {product.attributes && (
                              <div className="rm-commission-product-description">
                                {product.attributes}
                              </div>
                            )}
                          </div>
                          <div className="rm-commission-input-group">
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder={isCardProduct ? "e.g., 500" : "e.g., 5.5"}
                              className="rm-commission-input"
                              value={commissionInputs[product.id] || ''}
                              onChange={(e) => handleCommissionChange(product.id, e.target.value, isCardProduct)}
                              disabled={savingCommissions}
                              aria-label={`Commission ${isCardProduct ? 'amount' : 'percentage'} for ${product.name}`}
                            />
                            <span className="rm-commission-percent-sign">
                              {isCardProduct ? "₹" : "%"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            <div className="rm-commission-save-action">
              <button
                className={`rm-btn rm-btn-primary ${savingCommissions ? 'rm-btn-loading' : ''}`}
                onClick={handleSaveCommissions}
                disabled={savingCommissions || productsLoading || commissionsLoading}
              >
                {savingCommissions ? (
                  <>
                    <FaSpinner className="rm-spinner rm-spinner-small" /> Saving Changes...
                  </>
                ) : (
                  <>
                    <FaSave /> Save Commissions
                  </>
                )}
              </button>
              {filteredProducts.length > 10 && (
                <div className="rm-save-hint">
                  All commission changes will be saved at once
                </div>
              )}
            </div>
          </div>
        );
      default:
        return <p>Select a tab to view details</p>;
    }
  };

  // Tab navigation items
  // Tab navigation items (updated)
  const tabItems = [
    { id: 'profile', label: 'Profile', icon: <FaUser size={16} /> },
    { id: 'documents', label: 'Documents', icon: <FaFileAlt size={16} /> },
    { id: 'agreement', label: 'Agreement', icon: <FaFileContract size={16} /> },
    { id: 'agents', label: 'Agents', icon: <FaUsers size={16} /> },
    { id: 'commissions', label: 'Commissions', icon: <FaPercent size={16} /> }
  ];

  return (
    <div className="rm-owner-details-page">
      <div className="rm-owner-details-content">
        <div className="rm-detail-tabs">
          {tabItems.map(tab => (
            <div
              key={tab.id}
              className={`rm-detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                  setActiveTab(tab.id);
                  // Clear save messages when switching tabs
                  setSaveError('');
              }}
            >
              <span className="rm-tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </div>
          ))}
          {/* --- Modify this element --- */}
          {ownerDetails && (
            <div className="rm-detail-owner-name">
              <FaUserCircle className="rm-owner-icon" /> {/* Add icon */}
              <span>{ownerDetails.name || 'DSO Details'}</span> {/* Wrap text */}
            </div>
          )}
          {/* --- End of modified element --- */}
        </div>
        
        <div className="rm-detail-tab-content">
          {renderTabContent()}
        </div>
      </div>

      {/* --- Add styles for agents table and smaller loading/error states --- */}
      <style jsx>{`
        /* ... (keep all existing styles) ... */
        .rm-owner-details-page {
          padding: 0;
          color: #1e293b;
        }
        
        .rm-owner-details-content {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        /* Tab styling */
        .rm-detail-tabs {
          display: flex; /* Use flexbox */
          align-items: center; /* Align items vertically */
          border-bottom: 1px solid #f1f5f9;
          background-color: #0a2540; /* Dark background */
          padding-right: 10px; /* Add some padding on the right */
          /* Remove flex-direction: column if it exists from previous responsive styles */
        }
        
        .rm-detail-tab {
          padding: 16px 20px;
          font-size: 0.9rem;
          font-weight: 500;
          color: #f1f5f9;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }
        
        .rm-detail-tab:hover {
          color: #1e293b;
          background-color: #f1f5f9;
        }
        
        .rm-detail-tab.active {
          color: #0a2540;
          border-bottom-color: #0a2540;
          background-color: white;
        }
        
        .rm-tab-icon {
          display: flex;
          align-items: center;
        }
        
        .rm-detail-tab-content {
          padding: 24px;
        }
        
        /* Content styling */
        .rm-owner-detail-content h4 {
          margin-top: 0;
          margin-bottom: 20px;
          color: #334155;
          font-size: 1.1rem;
          font-weight: 600;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 12px;
        }
        
        .rm-profile-details {
          max-width: 1000px;
        }
        
        /* Two-column layout for profile details */
        .rm-profile-two-column {
          display: flex;
          flex-wrap: wrap;
          gap: 30px;
        }
        
        .rm-profile-column {
          flex: 1;
          min-width: 300px;
        }
        
        .rm-column-heading {
          font-size: 0.95rem;
          font-weight: 600;
          color: #334155;
          margin: 24px 0 16px 0;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .rm-column-heading:first-child {
          margin-top: 0;
        }
        
        .rm-detail-item {
          margin-bottom: 16px;
        }
        
        .rm-detail-label {
          display: block;
          font-weight: 600;
          color: #475569;
          margin-bottom: 8px;
          font-size: 0.9rem;
        }
        
        .rm-detail-value {
          display: block;
          color: #1e293b;
          font-size: 0.95rem;
          padding: 10px 12px;
          background-color: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        
        /* Documents table */
        .rm-documents-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
          margin-top: 16px;
        }
        
        .rm-documents-table th {
          text-align: left;
          padding: 12px 16px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .rm-documents-table td {
          padding: 16px;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-documents-table tr:last-child td {
          border-bottom: none;
        }
        
        .rm-view-doc-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #f1f5f9;
          color: #1e293b;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          padding: 6px 12px;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }
        
        .rm-view-doc-btn:hover {
          background: #e2e8f0;
        }
        
        /* Button styling */
        .rm-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border-radius: 6px;
          padding: 8px 16px;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }
        
        .rm-btn-primary {
          background-color: #0a2540;
          color: white;
          border: none;
        }
        
        .rm-btn-primary:hover {
          background-color: #0f3a5f;
        }
        
        .rm-btn-secondary {
          background-color: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .rm-btn-secondary:hover:not([disabled]) {
          background-color: #e2e8f0;
          color: #475569;
        }
        
        .rm-btn-secondary[disabled] {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        /* --- Update styles for the owner name badge --- */
        .rm-detail-owner-name {
          margin-left: auto; /* Push to the right */
          display: inline-flex; /* Use inline-flex */
          align-items: center; /* Vertically align icon and text */
          gap: 8px; /* Space between icon and text */
          background-color: #1e3a5f; /* Slightly lighter than tab background */
          color: #e2e8f0; /* Light text color */
          padding: 8px 16px; /* Adjust padding */
          border-radius: 20px; /* Rounded corners for badge effect */
          font-size: 0.9rem; /* Adjust font size */
          font-weight: 500; /* Adjust font weight */
          white-space: nowrap; /* Prevent wrapping */
        }

        .rm-owner-icon {
          font-size: 1.1rem; /* Adjust icon size */
          color: #94a3b8; /* Slightly muted icon color */
        }
        /* --- End of updated styles --- */
        
        /* Status badges */
        .rm-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 10px;
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 4px;
        }
        
        .rm-icon-small {
          font-size: 10px;
        }
        
        .rm-status-verified {
          background-color: #10b981;
        }
        
        .rm-status-active {
          background-color: #3b82f6;
        }
        
        .rm-status-pending {
          background-color: #f59e0b;
        }
        
        .rm-status-inactive {
          background-color: #64748b;
        }
        
        /* State displays */
        .rm-loading-state,
        .rm-error-state,
        .rm-no-data {
          padding: 40px 0;
          text-align: center;
          color: #64748b;
          font-size: 0.95rem;
        }
        
        .rm-loading-state,
        .rm-error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }
        
        .rm-spinner {
          font-size: 1.5rem;
          color: #6366f1;
          animation: spin 1s linear infinite;
        }
        
        .rm-error-icon {
          font-size: 1.5rem;
          color: #ef4444;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          /* Keep the column layout for tabs on small screens if desired */
          .rm-detail-tabs {
             flex-direction: column;
             align-items: stretch; /* Stretch items to full width */
             padding-right: 0; /* Remove right padding */
          }

          .rm-detail-tab {
            border-bottom: 1px solid #1e3a5f; /* Darker border for dark bg */
            border-left: 2px solid transparent;
          }

          .rm-detail-tab.active {
            border-bottom-color: #1e3a5f;
            border-left-color: #6366f1; /* Example active color */
            background-color: #1e3a5f; /* Slightly lighter active bg */
          }

          /* Adjust owner name badge for mobile */
          .rm-detail-owner-name {
             margin-left: 0; /* Reset margin */
             margin: 10px; /* Add some margin */
             align-self: flex-end; /* Align to the right within the column */
             border-radius: 6px; /* Less rounded on mobile? Or keep 20px */
             padding: 8px 12px;
             font-size: 0.85rem;
             /* Remove border-top if it existed */
          }
          
          /* Adjust two-column layout for small screens */
          .rm-profile-two-column {
            flex-direction: column;
            gap: 0;
          }
          
          .rm-profile-column {
            width: 100%;
          }
          
          .rm-column-heading {
            margin-top: 20px;
          }
          
          /* Commission product items for mobile */
          .rm-commission-product-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          
          .rm-commission-product-image {
            width: 80px;
            height: 80px;
            margin-right: 0;
          }
          
          .rm-commission-product-info {
            width: 100%;
          }
          
          .rm-commission-input-group {
            width: 100%;
            margin-left: 0;
          }
          
          .rm-commission-product-details {
            width: 100%;
          }
        }

        /* Commission specific styling */
        .rm-commissions-tab {
          width: 100%;
        }
        
        .rm-commission-instructions {
          margin-bottom: 20px;
          color: #475569;
          font-size: 0.95rem;
        }
        
        .rm-commission-filters {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 16px;
        }
        
        .rm-product-count {
          font-size: 0.9rem;
          color: #64748b;
        }
        
        .rm-bank-filter {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .rm-bank-filter label {
          font-size: 0.9rem;
          font-weight: 500;
          color: #334155;
        }
        
        .rm-bank-filter select {
          padding: 8px 12px;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          background-color: white;
          color: #1e293b;
          font-size: 0.9rem;
          min-width: 150px;
          cursor: pointer;
        }
        
        .rm-bank-filter select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        
        .rm-commission-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 24px;
        }
        
        .rm-commission-product-item {
          display: flex;
          align-items: center;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 16px;
          background-color: #f8fafc;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .rm-commission-product-image {
          flex: 0 0 100px;
          height: 100px;
          margin-right: 20px;
          background-color: white;
          border-radius: 6px;
          padding: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        
        .rm-commission-product-image img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        
        .rm-placeholder-icon {
          font-size: 36px;
          color: #cbd5e1;
        }
        
        .rm-commission-product-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
        }
        
        .rm-commission-product-name-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .rm-commission-product-name {
          font-size: 1rem;
          font-weight: 600;
          color: #334155;
          margin: 0;
        }
        
        .rm-apply-link {
          color: #6366f1;
          transition: color 0.2s ease, transform 0.2s ease;
          display: inline-flex;
          padding: 3px;
          border-radius: 4px;
        }
        
        .rm-apply-link:hover {
          color: #4f46e5;
          background-color: #e0e7ff;
          transform: translateY(-2px);
        }
        
        .rm-commission-product-details {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 0.85rem;
        }
        
        .rm-commission-product-bank,
        .rm-commission-product-category,
        .rm-commission-product-type,
        .rm-commission-product-fees {
          padding: 4px 8px;
          border-radius: 4px;
          background-color: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #334155;
          white-space: nowrap;
        }
        
        .rm-commission-product-description {
          font-size: 0.85rem;
          color: #64748b;
          margin-top: 4px;
          line-height: 1.4;
          max-width: 500px;
        }
        
        .rm-commission-input-group {
          flex: 0 0 120px;
          position: relative;
          margin-left: 20px;
        }
        
        .rm-commission-input {
          width: 100%;
          padding: 8px;
          padding-right: 30px; /* Room for percent sign */
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          font-size: 0.95rem;
          text-align: right;
          appearance: none;
          -moz-appearance: textfield; /* Firefox */
        }
        
        .rm-commission-input:focus {
          border-color: #3b82f6;
          outline: none;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        
        .rm-commission-percent-sign {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #475569;
          font-weight: 600;
          pointer-events: none;
        }
        
        .rm-commission-save-action {
          margin-top: 24px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        
        .rm-save-hint {
          font-size: 0.8rem;
          color: #64748b;
          margin-top: 8px;
          font-style: italic;
        }
        
        .rm-spinner-small {
          font-size: 0.8rem;
          margin-right: 8px;
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        
        .rm-btn-loading {
          position: relative;
          background-color: #0f3a5f !important;
          cursor: wait;
        }
        
        .rm-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 160px;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .rm-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        
        .rm-alert {
          padding: 14px 16px;
          margin-bottom: 20px;
          border-radius: 6px;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          animation: fadeIn 0.3s ease-in-out;
        }
        
        .rm-alert-success {
          background-color: #dcfce7;
          color: #166534;
          border: 1px solid #a7f3d0;
        }
        
        .rm-alert-success::before {
          content: "✓";
          display: inline-block;
          margin-right: 10px;
          font-weight: bold;
          background-color: #16a34a;
          color: white;
          width: 20px;
          height: 20px;
          text-align: center;
          line-height: 20px;
          border-radius: 50%;
        }
        
        .rm-alert-danger {
          background-color: #fee2e2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }
        
        .rm-alert-danger::before {
          content: "!";
          display: inline-block;
          margin-right: 10px;
          font-weight: bold;
          background-color: #dc2626;
          color: white;
          width: 20px;
          height: 20px;
          text-align: center;
          line-height: 20px;
          border-radius: 50%;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Agents table styling (similar to documents table) */
        .rm-agents-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
          margin-top: 16px;
        }

        .rm-agents-table th {
          text-align: left;
          padding: 12px 16px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .rm-agents-table td {
          padding: 16px;
          border-bottom: 1px solid #f1f5f9;
          color: #1e293b; /* Ensure text color */
        }

        .rm-agents-table tr:last-child td {
          border-bottom: none;
        }

        /* Smaller loading/error states for inline use */
        .rm-loading-state-small,
        .rm-error-state-small {
           padding: 20px 0;
           font-size: 0.9rem;
           text-align: center; /* Center align */
           color: #64748b; /* Match other states */
           display: flex; /* Use flex for centering icon/text */
           flex-direction: column;
           align-items: center;
           gap: 8px; /* Space between icon and text */
        }
        .rm-loading-state-small .rm-spinner,
        .rm-error-state-small .rm-error-icon {
            font-size: 1.2rem;
            /* margin-bottom: 8px; /* Replaced by gap */
        }

        /* Add styles specifically for the commissions tab */
        .rm-commissions-tab h4 {
           /* Styles for the commission tab title if needed */
           /* Example: border-bottom: none; padding-bottom: 0; */
        }

        .rm-commission-instructions {
            font-size: 0.9rem;
            color: #475569; /* Slightly darker than default text */
            margin-bottom: 24px;
            background-color: #f8fafc;
            padding: 12px 16px;
            border-radius: 6px;
            border: 1px solid #e2e8f0;
        }

        .rm-commission-form {
            display: flex;
            flex-direction: column;
            gap: 16px; /* Space between product items */
            margin-bottom: 24px;
        }

        .rm-commission-product-item {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 16px;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            background-color: #fff;
            transition: box-shadow 0.2s ease-in-out;
        }
        .rm-commission-product-item:focus-within { /* Highlight when input inside is focused */
             box-shadow: 0 0 0 2px #c7d2fe; /* Example focus ring */
             border-color: #818cf8;
        }


        .rm-commission-product-image {
            flex-shrink: 0;
            width: 60px;
            height: 60px;
            background-color: #f1f5f9;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden; /* Ensure image fits */
            border: 1px solid #e2e8f0;
        }

        .rm-commission-product-image img {
            max-width: 100%;
            max-height: 100%;
            object-fit: cover; /* Or 'contain' */
        }

        .rm-commission-product-image .rm-placeholder-icon {
            font-size: 24px;
            color: #94a3b8;
        }

        .rm-commission-product-info {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            gap: 2px; /* Small gap between name and category */
        }

        .rm-commission-product-name {
            font-weight: 600;
            color: #1e293b;
            font-size: 1rem;
            /* margin-bottom: 4px; Replaced by gap */
        }

        .rm-commission-product-category {
            font-size: 0.85rem;
            color: #64748b;
        }

        .rm-commission-input-group {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .rm-commission-input {
            width: 80px; /* Adjust width as needed */
            padding: 8px 12px;
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            font-size: 0.95rem;
            text-align: right;
            background-color: #fff;
             transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
            /* Remove spinner arrows for number input if type="number" was used */
            -moz-appearance: textfield;
        }
        .rm-commission-input::-webkit-outer-spin-button,
        .rm-commission-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        .rm-commission-input:focus {
            outline: none;
            border-color: #818cf8; /* Example focus color */
            box-shadow: 0 0 0 1px #818cf8;
        }
        .rm-commission-input:disabled {
            background-color: #f1f5f9;
            cursor: not-allowed;
            opacity: 0.7;
        }


        .rm-commission-percent-sign {
            font-size: 1rem;
            color: #475569;
            font-weight: 500;
        }

        .rm-commission-save-action {
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            text-align: right; /* Align button to the right */
        }

        /* Small spinner for button */
        .rm-spinner-small {
            display: inline-block; /* Needed for animation */
            width: 1em; /* Size relative to font */
            height: 1em;
            vertical-align: -0.125em; /* Align better with text */
            border: 0.15em solid currentColor; /* Use button text color */
            border-right-color: transparent;
            border-radius: 50%;
            animation: spin 0.75s linear infinite;
            margin-right: 6px; /* Space between spinner and text */
        }

        /* Alert styles (simple example) */
        .rm-alert {
            padding: 12px 16px;
            margin-bottom: 16px;
            border-radius: 6px; /* Match other elements */
            font-size: 0.9rem;
            border: 1px solid transparent; /* Base border */
        }
        .rm-alert-success {
            background-color: #f0fdf4; /* Lighter green */
            color: #15803d; /* Darker green text */
            border-color: #bbf7d0; /* Light green border */
        }
        .rm-alert-danger {
            background-color: #fef2f2; /* Lighter red */
            color: #b91c1c; /* Darker red text */
            border-color: #fecaca; /* Light red border */
        }

        /* Responsive adjustments if needed */
        @media (max-width: 600px) {
            .rm-commission-product-item {
                flex-direction: column;
                align-items: flex-start; /* Align items to start */
                gap: 12px; /* Adjust gap for column layout */
            }
            .rm-commission-input-group {
                margin-top: 10px;
                width: 100%; /* Make input group take full width */
                justify-content: flex-end; /* Align input to the right */
            }
            .rm-commission-input {
                width: 100px; /* Adjust width */
            }
            .rm-commission-save-action {
                text-align: center; /* Center button on small screens */
            }
            .rm-commission-save-action .rm-btn {
                width: 100%; /* Make button full width */
                justify-content: center; /* Center button text/icon */
            }
        }

        /* Category tabs styling for commissions */
        .rm-commission-category-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 1px;
        }
        
        .rm-commission-category-tab {
          padding: 10px 16px;
          font-size: 0.9rem;
          font-weight: 500;
          color: #64748b;
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          top: 1px;
          margin-right: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .rm-commission-category-tab:hover {
          background-color: #f1f5f9;
          color: #334155;
        }
        
        .rm-commission-category-tab.active {
          background-color: white;
          color: #0a2540;
          font-weight: 600;
          border-bottom: 1px solid white;
        }
        
        .rm-commission-tab-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background-color: #e2e8f0;
          color: #475569;
          font-size: 0.75rem;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 10px;
        }
        
        .rm-commission-category-tab.active .rm-commission-tab-count {
          background-color: #0a2540;
          color: white;
        }
      `}</style>
    </div>
  );
}

export default OwnerDetails;
